/**
 * member.ts — response-SHAPE gate schemas for the MEMBER-KEYED CLINICAL sections. These feed
 * fetchEnvelope's optional `schema` param so STRUCTURAL render-shape drift (array vs non-array, a
 * missing REQUIRED field, a wrong PRIMITIVE type) is caught at the API boundary instead of throwing
 * deep in a grid / tab / timeline render. They are NOT value validators — see base.ts LENIENCY
 * CONVENTIONS (primitives only; `| null` => .nullable(); optional => .optional(); no enum/literal
 * value-policing; no .strict(); objects keep extra server keys).
 *
 * Endpoints backed here (confirmed against the pages that call them):
 *   Member360.tsx   GET /participants/:key/360                    -> Member360 (record of Section<unknown>)
 *   Vitals.tsx      GET /participants/:key/vitals?pageSize=100    -> Vital[]
 *   Medications.tsx GET /participants/:key/medications            -> Medication[]
 *                   GET /participants/:key/medications/safety     -> MedicationSafety
 *   Encounters.tsx  GET /participants/:key/encounters?pageSize=100-> Encounter[]
 *   Utilization.tsx GET /participants/:key/utilization            -> UtilizationEvent[]
 *   Idt.tsx         GET /participants/:key/care-plan              -> CarePlanBundle
 *                   GET /participants/:key/idt-team               -> IdtMember[]
 *                   GET /idt/compliance-timers                    -> ComplianceTimer[]
 *
 * Each schema carries an AssertExtends guard proving the CONTRACT type is assignable to the schema's
 * inferred type (the schema is never STRICTER than the contract — no false-positive rejection of valid
 * server data). Primitives are intentionally WIDER than the contract's string-literal unions (rule 3).
 */
import { z } from "zod";
import type {
  Vital,
  Medication,
  MedicationSafety,
  Encounter,
  UtilizationEvent,
  CarePlan,
  Goal,
  CarePlanBundle,
  IdtMember,
  ComplianceTimer,
  Member360,
} from "@shared/contracts";
import { provenancedRowShape, sectionSchema, type AssertExtends } from "./base";

/**
 * Vital (#14/#15) — op_fact_observation LEFT JOIN gold dim_code. Extends ProvenancedRow. code_key is a
 * required opaque surrogate string; every descriptive/measured field is required-but-nullable; the two
 * component readings and the coded flag are nullable primitives.
 */
export const vitalSchema = z.object({
  ...provenancedRowShape,
  code_key: z.string(),
  code_display: z.string().nullable(),
  loinc_code: z.string().nullable(),
  code_system: z.string().nullable(),
  effective_date: z.string().nullable(),
  value_quantity: z.number().nullable(),
  value_unit: z.string().nullable(),
  value_string: z.string().nullable(),
  component_systolic: z.number().nullable(),
  component_diastolic: z.number().nullable(),
  is_coded_flag: z.boolean().nullable(),
});
type _VitalLink = AssertExtends<Vital, z.infer<typeof vitalSchema>>;

/** The vitals endpoint returns a bare array of rows (the envelope `data` is the array itself). */
export const vitalListSchema = z.array(vitalSchema);

/**
 * Medication (#16) — LIVE silver MedicationRequest. Extends ProvenancedRow. `id` is the required opaque
 * row key; `drug_name_status` is the contract union "real" | "deferred" collapsed to a plain z.string()
 * (rule 3 — never value-policed); the coded drug / order fields are required-but-nullable.
 */
export const medicationSchema = z.object({
  ...provenancedRowShape,
  id: z.string(),
  medication_display: z.string().nullable(),
  drug_name_status: z.string(),
  rxnorm_code: z.string().nullable(),
  status: z.string().nullable(),
  intent: z.string().nullable(),
  authored_on: z.string().nullable(),
  days_supply: z.number().nullable(),
  dose_quantity: z.number().nullable(),
  dose_unit: z.string().nullable(),
});
type _MedicationLink = AssertExtends<Medication, z.infer<typeof medicationSchema>>;

/**
 * The medications endpoint returns a bare array of orders. `drug_name_status` is the contract union
 * "real" | "deferred" deliberately WIDENED to z.string() (leniency rule 3 — never value-policed), which
 * makes the inferred output wider than `Medication[]`; the AssertExtends guard above already proves the
 * CONTRACT is assignable to the (looser) schema, so the schema never rejects valid server data. Cast to
 * the contract's ZodType (the same technique base.ts uses for sectionSchema) so usePage<Medication[]>
 * accepts it. The gate still safeParses the real ZodArray at runtime and returns the original payload.
 */
export const medicationListSchema = z.array(medicationSchema) as unknown as z.ZodType<Medication[]>;

/**
 * MedicationSafety (#17, MIXED) — two self-provenanced sections. polypharmacy carries a REAL count;
 * beers carries the MOCK (always-empty until seeded) flags list. Composed from the shared sectionSchema
 * so a section that loses its `status`/`provenance` envelope is caught as structural drift.
 */
export const medicationSafetySchema = z.object({
  polypharmacy: sectionSchema(z.object({ count: z.number() })),
  beers: sectionSchema(z.object({ flags: z.array(z.unknown()) })),
});
type _MedicationSafetyLink = AssertExtends<MedicationSafety, z.infer<typeof medicationSafetySchema>>;

/**
 * Encounter (#18) — op_fact_encounter LEFT JOIN gold dim_practitioner. Extends ProvenancedRow. The date
 * key is a nullable number (the timeline groups by year off it); every other field is required-but-
 * nullable; the telehealth + PCP flags are nullable booleans.
 */
export const encounterSchema = z.object({
  ...provenancedRowShape,
  period_start: z.string().nullable(),
  period_start_date_key: z.number().nullable(),
  encounter_class: z.string().nullable(),
  encounter_type_code: z.string().nullable(),
  encounter_type_display: z.string().nullable(),
  cpt_or_hcpcs_code: z.string().nullable(),
  place_of_service: z.string().nullable(),
  is_telehealth_flag: z.boolean().nullable(),
  practitioner_first_name: z.string().nullable(),
  practitioner_last_name: z.string().nullable(),
  practitioner_is_pcp: z.boolean().nullable(),
});
type _EncounterLink = AssertExtends<Encounter, z.infer<typeof encounterSchema>>;

/** The encounters endpoint returns a bare array of visits. */
export const encounterListSchema = z.array(encounterSchema);

/**
 * UtilizationEvent (#19, MOCK) — op_fact_utilization_event. Extends ProvenancedRow; every event field is
 * required-but-nullable. The synthetic per-patient details are withheld from render, but the SHAPE gate
 * still guards the array contract so a drifted body degrades to the honest holding state, never a crash.
 */
export const utilizationEventSchema = z.object({
  ...provenancedRowShape,
  event_class: z.string().nullable(),
  admit_date: z.string().nullable(),
  discharge_date: z.string().nullable(),
  facility_npi: z.string().nullable(),
  primary_diagnosis_code: z.string().nullable(),
});
type _UtilizationLink = AssertExtends<UtilizationEvent, z.infer<typeof utilizationEventSchema>>;

/** The utilization endpoint returns a bare array of events. */
export const utilizationListSchema = z.array(utilizationEventSchema);

/**
 * CarePlan (#27) — op_fact_care_plan (coded-only). Extends ProvenancedRow; every coded field is
 * required-but-nullable (NO free-text title/narrative field exists on the contract).
 */
export const carePlanSchema = z.object({
  ...provenancedRowShape,
  status: z.string().nullable(),
  category_code: z.string().nullable(),
  intent: z.string().nullable(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
});
type _CarePlanLink = AssertExtends<CarePlan, z.infer<typeof carePlanSchema>>;

/** Goal (#27) — op_fact_goal (coded category). Extends ProvenancedRow; fields required-but-nullable. */
export const goalSchema = z.object({
  ...provenancedRowShape,
  lifecycle_status: z.string().nullable(),
  achievement_status: z.string().nullable(),
  description: z.string().nullable(),
  due_date: z.string().nullable(),
});
type _GoalLink = AssertExtends<Goal, z.infer<typeof goalSchema>>;

/**
 * CarePlanBundle — the /care-plan payload: two arrays the page maps over. The gate makes a non-array
 * carePlans/goals a boundary rejection (routes to the recovery UI) instead of relying solely on the
 * page's defensive Array.isArray collapse.
 */
export const carePlanBundleSchema = z.object({
  carePlans: z.array(carePlanSchema),
  goals: z.array(goalSchema),
});
type _CarePlanBundleLink = AssertExtends<CarePlanBundle, z.infer<typeof carePlanBundleSchema>>;

/**
 * IdtMember (#28, MOCK) — op_dim_provider (+roster). Extends ProvenancedRow; npi/name/role are
 * required-but-nullable. Rendered behind a DEFERRED caveat, but the array contract is still gated.
 */
export const idtMemberSchema = z.object({
  ...provenancedRowShape,
  npi: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  primary_specialty_text: z.string().nullable(),
  idt_role: z.string().nullable(),
});
type _IdtMemberLink = AssertExtends<IdtMember, z.infer<typeof idtMemberSchema>>;

/** The idt-team endpoint returns a bare array of team members. */
export const idtMemberListSchema = z.array(idtMemberSchema);

/**
 * ComplianceTimer (#29, DEFERRED) — fact_assessment absent, served deferred. Extends ProvenancedRow;
 * cadence/due_date are required-but-nullable.
 */
export const complianceTimerSchema = z.object({
  ...provenancedRowShape,
  cadence: z.string().nullable(),
  due_date: z.string().nullable(),
});
type _ComplianceTimerLink = AssertExtends<ComplianceTimer, z.infer<typeof complianceTimerSchema>>;

/** The compliance-timers endpoint returns a bare array (empty in the DEFERRED holding state). */
export const complianceTimerListSchema = z.array(complianceTimerSchema);

/**
 * Member360 (#3) — a record of named, self-provenanced sections (identity + the requested domain
 * sections). Each section's INNER data is intentionally z.unknown() (the composite mixes arrays and
 * objects per section, and each section already carries its own status/provenance the page reads); the
 * gate asserts the outer record-of-Section SHAPE so a drifted section (missing status/provenance, or a
 * non-object where a section is expected) is caught at the boundary while a legitimately-empty or
 * unusually-shaped section payload still passes (the page's per-section guards handle the inner render).
 *
 * PHI-SAFE PATHS (base.ts convention): the section KEYS are PAYLOAD-DERIVED (a member/participant id can
 * appear as a key), and a plain `z.record(z.string(), sectionSchema(...))` would surface that KEY as the
 * first element of a failing issue.path — which api.ts logs. So instead we validate the top level as a
 * record of UNKNOWN values and safeParse each value against sectionSchema in a superRefine, emitting a
 * STATIC, non-PHI path token ("section") and a static message on failure. This keeps the drift signal (a
 * malformed section still fails safeParse → routes the page to its recovery UI) while guaranteeing no
 * payload key or value ever enters issue.path or issue.message. (api.ts also sanitizes paths as a second
 * line of defense.) The cast mirrors medicationListSchema so usePage<Member360> accepts the widened schema.
 */
const member360SectionSchema = sectionSchema(z.unknown());
export const member360Schema = z
  .record(z.string(), z.unknown())
  .superRefine((obj, ctx) => {
    for (const value of Object.values(obj)) {
      if (!member360SectionSchema.safeParse(value).success) {
        // STATIC path token + STATIC message ONLY — never the key or the value (both can be PHI).
        ctx.addIssue({ code: "custom", path: ["section"], message: "invalid_section" });
      }
    }
  }) as unknown as z.ZodType<Member360>;
// CONTRACT LINK (F1): the runtime export above is cast to z.ZodType<Member360>, so `z.infer` of it
// collapses to Member360 and an AssertExtends against it would be VACUOUS (Member360 extends Member360 —
// links nothing, never breaks on drift). Derive the guard from a PRE-CAST raw schema whose inferred type
// is genuinely the record-of-Section shape, so a drift in Section (base.ts sectionSchema) breaks the build.
const member360RawSchema = z.record(z.string(), member360SectionSchema);
type _Member360Link = AssertExtends<Member360, z.infer<typeof member360RawSchema>>;
